/* eslint-disable react-func/max-lines-per-function */
import React, { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash-es';
import { Toast } from 'common-package';
import CryptoJS from 'crypto-js';
import * as Sentry from '@sentry/react';
import * as SentryCapacitor from '@sentry/capacitor';
import { useDispatch, useSelector } from 'react-redux';
import lookie from 'lookie';
import { useNavigate } from 'react-router';
import service from '@/service';
import { selectedDeviceUuid, setStoreInfo } from '@/store/device';
import { eventBus } from '@/layout/BasicLayout';
import { CURRENT_ANDROID_ID, DEVICE_NOT_BIND, SIGNATURE_SECRET, TOKEN_IDENTIFY, UPPER_STATUS_CODE } from '@/constant';

interface LoginProps {}

interface LoginProviderValue {
  isLogin: boolean;
  setIsLogin: React.Dispatch<React.SetStateAction<boolean>>;
  handleLogin: () => Promise<void>;
}

const loginContext = createContext<LoginProviderValue>(null);

export const LoginProvider: React.FC<PropsWithChildren<LoginProps>> = ({ children }) => {
  const [isLogin, setIsLogin] = useState(false);
  const deviceUuid = useSelector(selectedDeviceUuid);
  const dispatch = useDispatch();
  const nav = useNavigate();

  const checkLogin = useCallback(() => {
    return new Promise(async (resolve, reject) => {
      try {
        await service.system.getExclusiveImages();
        setIsLogin(true);
        resolve('');
      } catch (e) {
        reject(e);
      }
    });
  }, []);

  const handleLogin = useCallback(async () => {
    const timestamp = new Date().getTime();
    const androidId = lookie.get(CURRENT_ANDROID_ID);
    const signature = CryptoJS.MD5(SIGNATURE_SECRET + androidId + timestamp).toString();
    const { data: token } = await service.user.login({
      signature,
      androidId,
      timestamp,
    });
    lookie.set(TOKEN_IDENTIFY, token);
    setIsLogin(true);
  }, []);

  /** 获取当前机器绑定状态 */
  const checkBindStatus = useCallback(async () => {
    const androidId = lookie.get(CURRENT_ANDROID_ID);
    try {
      /** 校验设备是否绑定 */
      const { data } = await service.system.getBoundStore(androidId);
      dispatch(setStoreInfo(data));
      await checkLogin();

      Sentry.setUser({ id: androidId });
      SentryCapacitor.setUser({ id: androidId });
    } catch (e) {
      if ([401, 403].includes(e?.code)) {
        lookie.remove(TOKEN_IDENTIFY);
        handleLogin();
      }

      if (e?.code === UPPER_STATUS_CODE.DeviceNotYetBound) {
        Toast.show(e?.message);
        nav('/device-binding', { replace: true });
      }
    }
  }, [checkLogin, dispatch, handleLogin, nav]);

  // 监听设备未绑定事件
  const handleOnDeviceNotBind = useMemo(
    () =>
      debounce(() => {
        nav('/device-binding', { replace: true });
      }, 1000),
    [nav]
  );

  useEffect(() => {
    eventBus.on(DEVICE_NOT_BIND, handleOnDeviceNotBind);

    return () => {
      eventBus.off(DEVICE_NOT_BIND, handleOnDeviceNotBind);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // 忽略模型页面
    if (deviceUuid) {
      checkBindStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceUuid]);

  return <loginContext.Provider value={{ isLogin, setIsLogin, handleLogin }} children={children} />;
};

function useLogin(): LoginProviderValue {
  const context = useContext(loginContext);

  if (!context) {
    // eslint-disable-next-line no-console
    console.error(`请在组件外使用LoginProvider`);
  }

  return context || ({} as any);
}

export default useLogin;
